/* global dataLayer */
import humps from 'humps'
import upperFirst from 'lodash/upperFirst'
import { delay } from 'redux-saga'
import { takeEvery, takeLatest, put, call } from 'redux-saga/effects'

import request from 'utils/request'
import getApiUrl from 'utils/getApiUrl'
import urlify from 'utils/urlify'

import {
  FILTER_NAME_MAP,
  ORDER_BY_FILTER,
  PRICE_FILTER,
  COUNTRY_FILTER,
  TASTE_PROFILE_FILTER
} from 'constants/filtering'
import { WINE_COLORS_NL } from 'constants/wines'
import {
  FETCH_QUESTIONS,
  fetchQuestionsSuccess,
  fetchQuestionsFailure,
  toggleFilter,
  UPDATE_PRICE_FILTER,
  TOGGLE_FILTER,
  SET_ORDER_BY
} from './actions'
import { createTasteProfileFilterEvent } from 'utils/tagManager'

export function * fetchQuestions () {
  let questions
  try {
    const fileName = 'questions.json'
    const url = getApiUrl(`/smaakprofielen/${fileName}`)
    questions = yield call(request, {url: url, withCredentials: false})
  } catch (err) {
    yield put(fetchQuestionsFailure(err))
  }
  // camelize the incoming keys
  const convertedQuestions = humps.camelizeKeys(questions)
  yield put(fetchQuestionsSuccess(convertedQuestions))
}

export function * watchFetchQuestions () {
  yield takeEvery(FETCH_QUESTIONS, fetchQuestions)
}

const updateGetParams = ({name: filterName, value, router, tasteProfiles}) => {
  const {location} = router
  const {pathname, query} = location
  const nextRoute = {
    pathname,
    query: {
      ...query
    }
  }
  let targetValue
  switch (filterName) {
    case TASTE_PROFILE_FILTER.NAME: {
      targetValue = WINE_COLORS_NL[tasteProfiles.find(({name}) => name === value).color]
      nextRoute.query[FILTER_NAME_MAP[filterName]] = targetValue
      break
    }

    case PRICE_FILTER.NAME: {
      const [min, max] = value
      const {OPTIONS} = PRICE_FILTER
      const minPriceParam = FILTER_NAME_MAP[filterName].min
      const maxPriceParam = FILTER_NAME_MAP[filterName].max
      if (min > OPTIONS[0]) {
        nextRoute.query[minPriceParam] = min
      } else {
        delete nextRoute.query[minPriceParam]
      }
      if (max < OPTIONS[OPTIONS.length - 1]) {
        nextRoute.query[maxPriceParam] = max
      } else {
        delete nextRoute.query[maxPriceParam]
      }
      break
    }

    case COUNTRY_FILTER.NAME: {
      const currentCountries = nextRoute.query[FILTER_NAME_MAP[filterName]]
      let setTo = ''
      if (currentCountries) {
        const joinString = COUNTRY_FILTER.JOIN_MULTIPLE_ON
        if (currentCountries.includes(value)) {
          const split = currentCountries.split(joinString)
          const indexToRemove = split.indexOf(value)
          split.splice(indexToRemove, 1)
          setTo = split.join(joinString)
        } else {
          setTo = `${value}${joinString}${currentCountries}`
        }
      } else {
        setTo = value
      }

      if (setTo) {
        nextRoute.query[FILTER_NAME_MAP[filterName]] = setTo
      } else {
        delete nextRoute.query[FILTER_NAME_MAP[filterName]]
      }
      break
    }

    case ORDER_BY_FILTER.NAME: {
      targetValue = urlify(ORDER_BY_FILTER.OPTIONS.find(({value: thisValue}) => thisValue === value).label)
      nextRoute.query[FILTER_NAME_MAP[filterName]] = targetValue
      break
    }

    default: {
      console.warn('no case for filter with name', filterName)
      return
    }
  }

  router.push(nextRoute)
}

function * delayedUpdatePriceFilter (action) {
  const {value, router} = action.payload
  yield call(delay, 200)
  yield put(toggleFilter({name: PRICE_FILTER.NAME, value, router}))
}

export function * watchUpdatePriceFilter () {
  yield takeLatest(UPDATE_PRICE_FILTER, delayedUpdatePriceFilter)
}

function * handleToggleFilter (action) {
  const {name} = action.payload
  if (name === PRICE_FILTER.NAME) {
    yield call(delay, 200)
  }
  yield call(() => updateGetParams(action.payload))
}

export function * watchToggleFilter () {
  yield takeEvery(TOGGLE_FILTER, handleToggleFilter)
  yield takeEvery(TOGGLE_FILTER, pushFilteringAnalyticsEvent)
}

export function * watchSetOrderBy () {
  yield takeEvery(SET_ORDER_BY, handleToggleFilter)
  yield takeEvery(SET_ORDER_BY, pushOrderByAnalyticsEvent)
}

export function * pushOrderByAnalyticsEvent (action) {
  const {value, name} = action.payload
  yield call(() => {
    dataLayer.push(createTasteProfileFilterEvent(name, value))
  })
}

export function * pushFilteringAnalyticsEvent (action) {
  const {name} = action.payload
  let {value} = action.payload
  if (name === TASTE_PROFILE_FILTER.NAME) {
    const colorIndex = action.payload.tasteProfiles
      .find(({name: filterValueName}) => filterValueName === value)
      .color
    value = `${upperFirst(WINE_COLORS_NL[colorIndex])} - ${value}`
  }
  yield call(() => {
    dataLayer.push(createTasteProfileFilterEvent(name, value.toString()))
  })
}

export default [
  watchFetchQuestions,
  watchUpdatePriceFilter,
  watchToggleFilter,
  watchSetOrderBy
]
